import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, errMsg } from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/auth/login", { email, password });
      setStep(2);
      toast.success("A verification code has been emailed to you.");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await API.post("/auth/verify-2fa", { email, code });
      navigate("/admin", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 px-4" data-testid="admin-login-page">
      <Seo title="Admin Login | Eileen Jenkins Florist" />
      <Card className="w-full max-w-md">
        <CardContent className="p-8">
          <p className="font-display text-2xl text-center text-primary">Eileen Jenkins Florist</p>
          <p className="text-sm text-muted-foreground text-center mt-1 mb-8">Admin Panel</p>

          {step === 1 ? (
            <form onSubmit={requestCode} className="space-y-5" data-testid="admin-login-form">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email</Label>
                <Input id="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" autoComplete="username" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password</Label>
                <Input id="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" autoComplete="current-password" />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={busy} data-testid="admin-login-submit-btn">
                {busy ? "Checking…" : "Continue"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-5" data-testid="admin-2fa-form">
              <p className="text-sm text-muted-foreground text-center">
                A 6-digit verification code has been emailed to <strong className="text-foreground">all admin email addresses</strong>. It expires in 10 minutes.
              </p>
              <div className="space-y-2">
                <Label htmlFor="admin-code">Verification code</Label>
                <Input
                  id="admin-code"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="text-center text-2xl tracking-[0.5em]"
                  data-testid="admin-code-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full" disabled={busy || code.length !== 6} data-testid="admin-verify-btn">
                {busy ? "Verifying…" : "Sign in"}
              </Button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-sm text-muted-foreground hover:text-primary" data-testid="admin-back-to-login">
                ← Back
              </button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
